import type {Ref} from 'vue';
import {computed, ref} from 'vue';
import type {ScreenMap} from 'ant-design-vue/es/_util/responsiveObserve';

export enum EBreakpoint {
  XXXL = 'xxxl', // > 2000px
  XXL = 'xxl', // > 1600px
  XL = 'xl', // > 1220px
  LG = 'lg', // > 992px
  MD = 'md', // > 768px
  SM = 'sm', // > 576px
  XS = 'xs', // < 480px
}

export const breakpointsMobile = [EBreakpoint.XS, EBreakpoint.SM];
export const breakpointsTablet = [EBreakpoint.MD, EBreakpoint.LG];
export const breakpointsDesktop = [
  EBreakpoint.XL,
  EBreakpoint.XXL,
  EBreakpoint.XXXL,
];

export const fillBreakpointGaps = <T>(
  breakpoints: Partial<Record<EBreakpoint, T>> & {[EBreakpoint.XS]: T},
): Record<EBreakpoint, T> => {
  const defaultBreakpoints = Object.values(EBreakpoint).reverse();

  return Object.fromEntries(
    defaultBreakpoints.map((item, index) => {
      const currentBreakpoint =
        defaultBreakpoints
          .slice(0, index + 1)
          .reverse()
          .find((val) => breakpoints[val]) ?? EBreakpoint.XS;

      return [item, breakpoints[currentBreakpoint]];
    }),
  ) as Record<EBreakpoint, T>;
};

export const useBreakpointState = () => {
  const breakpointMap: Ref<Partial<Record<EBreakpoint, boolean>>> = ref({});

  const breakpoint = computed<EBreakpoint>(() => {
    return (
      (Object.entries(breakpointMap.value)
        .reverse()
        .find(([, value]) => value)?.[0] as EBreakpoint) ?? EBreakpoint.SM
    );
  });

  const isXXXL = computed(() => breakpoint.value === EBreakpoint.XXXL);
  const isXXL = computed(() => breakpoint.value === EBreakpoint.XXL);
  const isXL = computed(() => breakpoint.value === EBreakpoint.XL);
  const isLG = computed(() => breakpoint.value === EBreakpoint.LG);
  const isMD = computed(() => breakpoint.value === EBreakpoint.MD);
  const isSM = computed(() => breakpoint.value === EBreakpoint.SM);
  const isXS = computed(() => breakpoint.value === EBreakpoint.XS);

  const isDesktop = computed(() =>
    breakpointsDesktop.includes(breakpoint.value),
  );
  const isTablet = computed(() => breakpointsTablet.includes(breakpoint.value));
  const isMobile = computed(() => breakpointsMobile.includes(breakpoint.value));

  return {
    breakpoint,

    isXXXL,
    isXXL,
    isXL,
    isLG,
    isMD,
    isSM,
    isXS,
    isDesktop,
    isTablet,
    isMobile,

    setBreakpointMap: (screenMap: ScreenMap) =>
      (breakpointMap.value = screenMap),
  };
};

export const clientBreakpointState = useBreakpointState();
